
import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Loader, Brain, Sparkles, AlertTriangle } from "lucide-react";
import { getTeamSizeGuidelines } from "./utils/teamSizeUtils";

interface ProjectRequirementsFormProps {
  selectedProject: any;
  requirements: string;
  setRequirements: (value: string) => void;
  teamSize: string;
  setTeamSize: (value: string) => void;
  isAnalyzing: boolean;
  apiError: string | null;
  onAnalyzeProject: () => void;
}

const ProjectRequirementsForm = ({
  selectedProject,
  requirements,
  setRequirements,
  teamSize,
  setTeamSize,
  isAnalyzing,
  apiError,
  onAnalyzeProject
}: ProjectRequirementsFormProps) => {
  return (
    <div className="flex-shrink-0 mb-2">
      <Card className="bg-secondary/50 backdrop-blur-lg border-2 border-primary rounded-lg shadow-lg shadow-purple-500/10">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2 py-3">
            <CardTitle className="text-white text-xl">AI Project Advisor (beta version)</CardTitle>
            <Badge variant="secondary" className="bg-primary/20 text-primary">
              AI Powered
            </Badge>
            
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {!selectedProject && (
            <div className="bg-yellow-500/10 p-3 rounded-lg border border-yellow-500/20">
              <p className="text-yellow-300">Please select a project from the Project Planning tab to continue</p>
            </div>
          )}

          <div className="space-y-3">
            <div className="flex items-center gap-6">
              <Label className="text-primary font-medium">Select expected team size:</Label>
              <RadioGroup value={teamSize} onValueChange={setTeamSize} className="flex flex-row space-x-3">
                <div className="flex items-center space-x-2 text-[12px]">
                  <RadioGroupItem value="small" id="small" className="border-primary text-primary" />
                  <Label htmlFor="small" className="text-white cursor-pointer text-[12px]">Small</Label>
                </div>
                <div className="flex items-center space-x-2 text-[12px]">
                  <RadioGroupItem value="medium" id="medium" className="border-primary text-primary" />
                  <Label htmlFor="medium" className="text-white cursor-pointer text-[12px]">Medium</Label>
                </div>
                <div className="flex items-center space-x-2 text-[12px]">
                  <RadioGroupItem value="large" id="large" className="border-primary text-primary" />
                  <Label htmlFor="large" className="text-white cursor-pointer text-[12px]">Large</Label>
                </div>
                <div className="flex items-center space-x-2 text-[12px]">
                  <RadioGroupItem value="verylarge" id="verylarge" className="border-primary text-primary" />
                  <Label htmlFor="verylarge" className="text-white cursor-pointer text-[12px]">Very Large</Label>
                </div>
              </RadioGroup>
            </div>
            <div className="bg-secondary/30 p-2 rounded-lg border border-primary/10">
              <p className="text-gray-300 text-sm">{getTeamSizeGuidelines(teamSize, selectedProject?.template, selectedProject?.duration).description}</p>
            </div>
          </div>
          
          <div className="space-y-2">
            <label className="text-primary font-medium">Detailed Project Requirements</label>
            <Textarea
              placeholder="Describe your project requirements in detail. Include objectives, constraints, specific skills needed, timeline requirements, budget considerations, and any other important factors..."
              value={requirements}
              onChange={(e) => setRequirements(e.target.value)}
              className="min-h-[150px] bg-secondary/50 border-primary/20 text-white text-[12px] placeholder-gray-400"
              disabled={!selectedProject}
            />
            <p className="text-[12px] text-white">Note: AI hallucination may occur. Validation of generated information is essential.</p>
          </div>

          {apiError && (
            <div className="bg-red-500/10 p-3 rounded-lg border border-red-500/20 flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-red-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-red-300 font-medium">Analysis Error</p>
                <p className="text-red-200 text-sm mt-1">{apiError}</p>
                {apiError.includes('OpenAI API key') && (
                  <p className="text-red-200 text-xs mt-2">
                    Please ensure your OpenAI API key is configured in your Supabase project settings.
                  </p>
                )}
              </div>
            </div>
          )}
          
          <div className="flex items-center gap-3">
            <Button
              onClick={onAnalyzeProject}
              disabled={!selectedProject || !requirements.trim() || isAnalyzing}
              className="bg-primary hover:bg-primary/90 text-black font-medium"
            >
              {isAnalyzing ? (
                <>
                  <Loader className="h-4 w-4 mr-2 animate-spin" />
                  Analyzing Project...
                </>
              ) : (
                <>
                  Analyze with AI
                </>
              )}
            </Button>
            {isAnalyzing && (
              <div className="flex items-center gap-2">
                <Loader className="h-8 w-8 animate-spin text-primary" />
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProjectRequirementsForm;
